import { createFileRoute } from '@tanstack/react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { OnboardingLinkCreatedDialog } from '../../-components/OnboardingLinkCreatedDialog';
import { SsoConfirmDialog } from '../../-components/SsoConfirmDialog';
import { SsoConnectionCard } from '../../-components/SsoConnectionCard';
import { SsoOnboardingLinksCard } from '../../-components/SsoOnboardingLinksCard';
import { SsoOrganizationHeader } from '../../-components/SsoOrganizationHeader';
import { SsoOrganizationSettingsCard } from '../../-components/SsoOrganizationSettingsCard';
import { ssoOrganizationQueryOptions } from '../../-hooks/useSsoOrganizationDetail';
import { useSsoOrganizationDetailPage } from '../../-hooks/useSsoOrganizationDetailPage';

export const Route = createFileRoute('/management/sso/(pages)/$id/')({
  // A failed prefetch must not blank the page — the component's own query
  // re-runs and surfaces the error inline.
  loader: ({ params }) =>
    queryClient
      .ensureQueryData(ssoOrganizationQueryOptions(params.id))
      .catch(() => undefined),
  component: SsoOrganizationPage,
});

function SsoOrganizationPage() {
  const { id } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'managementSso' });
  const page = useSsoOrganizationDetailPage(id);
  const { organization } = page;
  useBreadcrumbLabel(id, organization?.name);

  if (page.isLoading) {
    return (
      <div className='flex flex-col flex-1 min-h-0 overflow-y-auto p-6 gap-4 animate-pulse'>
        <div className='h-24 rounded-lg bg-muted' />
        <div className='h-48 rounded-lg bg-muted' />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className='p-6'>
        <Alert variant='destructive' className='py-2.5'>
          <AlertDescription>{t('detail.notFound')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <SsoOrganizationHeader
        organization={organization}
        onSwitch={page.askSwitch}
        onDelete={page.askDelete}
        isPending={page.isPending}
      />
      <div className='flex flex-col gap-4 p-4 @md:p-6'>
        <div className='grid grid-cols-1 @md:grid-cols-2 gap-4'>
          <SsoOrganizationSettingsCard page={page} />
          <SsoConnectionCard
            organization={organization}
            onRemove={page.askRemoveConnection}
            isPending={page.isPending}
          />
        </div>
        <SsoOnboardingLinksCard
          links={organization.onboardingLinks}
          onCreate={() => void page.createLink()}
          onRevoke={page.askRevoke}
          isPending={page.isPending}
        />
      </div>

      <OnboardingLinkCreatedDialog
        created={page.createdLink}
        onClose={page.closeCreatedLink}
      />
      <SsoConfirmDialog
        open={page.isSwitchOpen}
        title={t('switch.title')}
        description={t('switch.description', { name: organization.name })}
        confirmLabel={t('switch.confirm')}
        onCancel={page.cancelSwitch}
        onConfirm={() => void page.confirmSwitch()}
        isPending={page.isPending}
      />
      <SsoConfirmDialog
        open={page.isDeleteOpen}
        title={t('delete.title', { name: organization.name })}
        description={t(`delete.description.${organization.status}`)}
        confirmLabel={t('delete.confirm')}
        onCancel={page.cancelDelete}
        onConfirm={() => void page.confirmDelete()}
        isPending={page.isPending}
      />
      <SsoConfirmDialog
        open={page.isRemoveConnectionOpen}
        title={t('connection.removeTitle')}
        description={t('connection.removeDescription')}
        confirmLabel={t('connection.remove')}
        onCancel={page.cancelRemoveConnection}
        onConfirm={() => void page.confirmRemoveConnection()}
        isPending={page.isPending}
      />
      <SsoConfirmDialog
        open={page.pendingRevoke !== null}
        title={t('links.revokeTitle')}
        description={t('links.revokeDescription')}
        confirmLabel={t('links.revoke')}
        onCancel={page.cancelRevoke}
        onConfirm={() => void page.confirmRevoke()}
        isPending={page.isPending}
      />
    </div>
  );
}
